import type { StrictPaddingStyle } from '../util/types';

import { useI18n } from '../../shared/I18n';
import { Box } from '../Box';
import { CharacterCounter } from '../CharacterCounter';
import { FONT_FAMILY } from '../constants';
import { ErrorText } from '../ErrorText';
import { Flex } from '../Flex';
import { Close } from '../icons';
import { Label } from '../Label';
import { LiveRegion } from '../LiveRegion';
import { Text } from '../Text';
import { useTheme } from '../ThemeProvider';
import {
    buildInputAccessibilityProps,
    type InputAccessibilityProps,
} from '../util/accessibility/buildInputAccessibilityProps';
import { useDebounce } from '../util/useDebounce';

import React, { type ReactElement, useId, useRef, useMemo, useImperativeHandle } from 'react';
import {
    type DimensionValue,
    TouchableOpacity,
    StyleSheet,
    TextInput as RNTextInput,
    View,
    Keyboard,
    type KeyboardTypeOptions,
    type ReturnKeyTypeOptions,
    Platform,
    AccessibilityInfo,
    findNodeHandle,
    type AccessibilityRole,
    type AccessibilityState,
    type ViewStyle,
    type TextStyle,
} from 'react-native';

export interface MADSTextInputProps extends TextInputProps {
    /**
     * The height of the input field. If not provided the input will grow with the content.
     */
    textAreaHeight?: number;

    /**
     * Whether the input field should be multiline.
     */
    multiline?: boolean;

    /**
     * Used to display the remaining number of characters
     */
    counterLimit?: number;

    /**
     * Testing ID
     */
    testId?: string;
}

export interface TextInputProps {
    /**
     * Identifies that the field is disabled.
     */
    disabled?: boolean;

    /**
     * Identifies that the field is invalid.
     */
    invalid?: boolean;

    /**
     * Accessibility text to denote the field is an invalid state to screenreaders
     * Native iOS and Android do not have a concept of `invalid` like web
     * This should be set to the translated text of the word "Invalid"
     * If invalidText is not specified, the invalid text will default to English
     */
    invalidText?: string;

    /**
     * The label text for the field.
     */
    label?: string;

    /**
     * The error text for the field when invalid is `true` (renders below the children).
     */
    errorText?: string;

    /**
     * Optional prefix prepended to `errorText` for accessibility output.
     * Example: "Error: "
     */
    errorPrefix?: string;

    /**
     * The explainer text for the field (renders below the label).
     * A11Y: This is also used to instruct users how to fill out the field
     */
    explainerText?: string;

    /**
     * The placeholder text displayed when the input is empty.
     */
    placeholderText?: string;

    /**
     * The current value of the text field
     */
    value: string;

    /**
     * Called when text is input
     */
    onInput: (text: string) => void;

    /**
     * Whether this field is required, will be applied to the label.
     */
    required?: boolean;

    /**
     * Accessibility text to denote the field is required to screenreaders
     * Native iOS and Android do not have a concept of `required` like web
     * This should be set to the translated text of the word "Required"
     * If requiredText is not specified, the required text will default to English
     */
    requiredText?: string;

    /**
     * Text to display after the label to indicate the field is optional.
     * If provided and field is not required, displays the text after the label.
     */
    optionalText?: string;

    /**
     * Screen-reader-only accessible name for the input.
     * Use when you need an accessible name without a visible label.
     * If `label` is provided, it takes precedence over `ariaText`.
     */
    ariaText?: string;

    /**
     * Screen-reader-only accessible hint for the input.
     * Use when you need an accessible hint without a visible label.
     * Defaults to the `explainerText` if not provided.
     */
    accessibilityHint?: string;

    /**
     * Optional decorative icon before the input text.
     */
    contentBefore?: ReactElement;

    /**
     * Optional decorative icon after the input text.
     */
    contentAfter?: ReactElement;

    /**
     * Whether to show the clear button.
     */
    showClearButton?: boolean;

    /**
     * Width of the container as a string in percentage.
     * @default '100%'
     */
    width?: string;

    /**
     * Aria text for the clear button.
     */
    ariaClearTextDescription?: string;

    /**
     * Keyboard type for the text input.
     * @default 'default'
     */
    keyboardType?: KeyboardTypeOptions;

    /**
     * Controls auto-capitalization behavior for the input.
     * Set to 'none' for inputs where capitalization is undesirable (e.g. URLs, emails).
     * @default 'sentences' (platform default)
     */
    autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

    /**
     * Defines the padding style overrides.
     */
    sx?: StrictPaddingStyle;

    /**
     * Whether to focus the input on mount.
     */
    autoFocus?: boolean;

    /**
     * Called when the input is focused.
     */
    onFocus?: () => void;

    /**
     * Called when the input is blurred.
     */
    onBlur?: () => void;

    /**
     * Used to locate this view in end-to-end tests.
     */
    testID?: string;

    /**
     * An optional identifier which links a custom InputAccessoryView to this text input.
     * The InputAccessoryView is rendered above the keyboard when this text input is focused.
     * iOS only.
     */
    inputAccessoryViewID?: string;

    /**
     * Determines how the return key should look on the keyboard.
     * @default 'done' for single-line, undefined for multiline
     */
    returnKeyType?: ReturnKeyTypeOptions;

    /**
     * Allows getting a ref to the component instance.
     * Once the component unmounts, React will set `ref.current` to `null`
     * (or call the ref with `null` if you passed a callback ref).
     */
    ref?: React.Ref<RNTextInput>;

    /**
     * Accessibility role for the input.
     */
    accessibilityRole?: AccessibilityRole;

    /**
     * Accessibility state for the input.
     */
    accessibilityState?: AccessibilityState;

    /**
     * Identifies the element that is controlled by the input.
     * Maps to `aria-controls` on web.
     */
    accessibilityControls?: string;

    /**
     * Optional native ID for the input element.
     * Maps to `id` on web.
     */
    nativeID?: string;

    /**
     * ID of an element owned by this input.
     * Maps to `aria-owns` on web. Use when the owned element (e.g. a listbox)
     * is rendered in a portal outside the DOM subtree so screen readers can
     * associate it with this input for aria-activedescendant to work correctly.
     */
    accessibilityOwns?: string;
}

export function TextInput(props: TextInputProps): ReactElement {
    return <MADSTextInput {...props} />;
}

export function MADSTextInput({
    disabled = false,
    invalid,
    invalidText,
    label,
    ariaText,
    accessibilityHint,
    explainerText,
    errorText,
    errorPrefix,
    onFocus,
    onBlur,
    autoFocus,
    value,
    required,
    requiredText,
    optionalText,
    onInput,
    placeholderText,
    contentBefore,
    contentAfter,
    showClearButton,
    width = '100%',
    ariaClearTextDescription,
    keyboardType,
    autoCapitalize,
    textAreaHeight,
    multiline,
    counterLimit,
    inputAccessoryViewID,
    returnKeyType,
    testID = 'text-input',
    sx,
    ref,
    accessibilityRole,
    accessibilityState,
    accessibilityControls,
    accessibilityOwns,
    nativeID,
}: MADSTextInputProps): ReactElement {
    const theme = useTheme();
    const gt = useI18n();
    const styles = useMemo(
        () =>
            StyleSheet.create({
                container: {
                    alignSelf: 'center',
                    // Web-only: Prevents the default browser focus ring on React Native Web to avoid
                    // "double focus" with the Design System's custom focus states.
                    outlineStyle: Platform.OS === 'web' ? 'none' : undefined,
                } as unknown as ViewStyle,
                wrapper: {
                    borderRadius: theme.borderRadius.md,
                    overflow: 'hidden',
                    width: '100%',
                    // Web-only: Prevents the default browser focus ring on React Native Web.
                    outlineStyle: Platform.OS === 'web' ? 'none' : undefined,
                } as unknown as ViewStyle,
                wrapperDefault: {
                    borderWidth: 1,
                    padding: 3,
                    borderColor: theme.colors.borderAction,
                },
                wrapperDisabled: {
                    opacity: 0.4,
                },
                wrapperActive: {
                    borderColor: theme.colors.utilityFocus,
                    padding: 1,
                    borderWidth: 3,
                },
                wrapperInvalid: {
                    borderColor: theme.colors.borderStatusCriticalStrong,
                    borderWidth: 1,
                    padding: 3,
                },
                wrapperInvalidActive: {
                    borderColor: theme.colors.borderStatusCriticalStrong,
                    borderWidth: 3,
                    padding: 1,
                },
                input: {
                    paddingTop: theme.spacing.s3,
                    paddingBottom: theme.spacing.s3,
                    paddingLeft: theme.spacing.s3,
                    paddingRight: theme.spacing.s3,
                    textAlignVertical: 'top',
                    borderWidth: 0,
                    width: '100%',
                    ...Platform.select({
                        // Disable the default focus border in favor of the custom one that is part of this component.
                        web: {
                            outlineStyle: 'none',
                            borderStyle: 'none',
                        },
                    }),
                    fontSize: theme.fontSizes.f3,
                    fontFamily: FONT_FAMILY,
                    flex: 1,
                    // Web-only: Prevents the default browser focus ring on React Native Web.
                    outlineStyle: Platform.OS === 'web' ? 'none' : undefined,
                } as unknown as TextStyle,
                inputDisabled: {
                    color: theme.colors.foregroundStrong,
                },
                inputWithContentBefore: {
                    paddingLeft: 0,
                },
                inputWithContentAfter: {
                    paddingRight: theme.spacing.s3,
                },
                iconBefore: {
                    paddingLeft: theme.spacing.s2,
                    paddingRight: theme.spacing.s2,
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    alignItems: 'center',
                    textAlignVertical: 'bottom',
                    left: 0,
                    justifyContent: 'center',
                },
                iconAfter: {
                    paddingLeft: theme.spacing.s1,
                    paddingRight: theme.spacing.s2,
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    alignItems: 'center',
                    textAlignVertical: 'bottom',
                    right: 0,
                    justifyContent: 'center',
                },
                clearButton: {
                    paddingLeft: theme.spacing.s1,
                    paddingRight: theme.spacing.s2,
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    alignItems: 'center',
                    textAlignVertical: 'bottom',
                    right: 0,
                    justifyContent: 'center',
                },
                errorContainer: {
                    marginTop: theme.spacing.s1,
                    width: '100%',
                },
                errorContainerDisabled: {
                    opacity: 0.4,
                },
                labelContainer: {
                    marginBottom: theme.spacing.s2,
                    width: '100%',
                },
                labelDisabled: {
                    opacity: 0.4,
                },
                inputContainer: {
                    flexDirection: 'row',
                    width: '100%',
                    position: 'relative',
                    alignItems: 'center',
                },
                counterContainer: {
                    alignItems: 'flex-start',
                    flexDirection: 'row',
                    width: '100%',
                },
                srOnly: {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                },
            }),
        [theme],
    );

    const [isActive, setIsActive] = React.useState(false);
    const [showKeyboardOnFocus, setShowKeyboardOnFocus] = React.useState(true);
    const [clearAnnouncement, setClearAnnouncement] = React.useState('');
    const labelId = `MADS_TextInput_label_${useId()}`;
    const explainerTextId = `MADS_TextInput_explainerText_${useId()}`;
    const errorTextId = `MADS_TextInput_errorText_${useId()}`;
    const defaultInputId = `MADS_TextInput_input_${useId()}`;

    // Internal ref for managing keyboard behavior
    const internalRef = useRef<RNTextInput>(null);
    // Track if focus is programmatic (from submit) vs user interaction
    const isProgrammaticFocus = useRef(false);
    // Track timeout for cleanup on unmount
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Track blur timeout for clear button interaction
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearAnnounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Track if component is still mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);

    // Expose the internal ref to external ref if provided
    useImperativeHandle(ref, () => internalRef.current!, []);

    // Platform detection for conditional attribute rendering
    const is_iOS = Platform.OS === 'ios';
    const is_android = Platform.OS === 'android';

    const wrapperStyle = [
        styles.wrapper,
        !isActive && styles.wrapperDefault,
        disabled && styles.wrapperDisabled,
        isActive && !invalid && styles.wrapperActive,
        invalid && (isActive ? styles.wrapperInvalidActive : styles.wrapperInvalid),
    ];

    const containerStyle = {
        ...styles.container,
        width: width as DimensionValue,
        ...sx,
    };

    const effectiveClearButtonAccessibilityLabel = useMemo(() => {
        if (ariaClearTextDescription != null && ariaClearTextDescription !== '') {
            return ariaClearTextDescription;
        }
        if (label) {
            return gt.gettext('Clear {0}', [label]);
        }
        return gt.gettext('Clear text');
    }, [ariaClearTextDescription, label, gt]);

    const handleClearText = () => {
        // Cancel pending blur so input stays focused after clearing
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        if (clearAnnounceTimeoutRef.current) {
            clearTimeout(clearAnnounceTimeoutRef.current);
            clearAnnounceTimeoutRef.current = null;
        }
        const announceMsg = label
            ? gt.gettext('{0} cleared', [label])
            : gt.gettext('Field cleared');
        setClearAnnouncement(announceMsg);
        clearAnnounceTimeoutRef.current = setTimeout(() => {
            setClearAnnouncement('');
            clearAnnounceTimeoutRef.current = null;
        }, 500);
        if (onInput) {
            onInput('');
        }
        // Refocus the input after clearing
        internalRef.current?.focus();
    };

    const handleFocus = () => {
        setIsActive(true);
        // Re-enable keyboard on any non-programmatic focus (touch or accessibility)
        if (!showKeyboardOnFocus && !isProgrammaticFocus.current) {
            setShowKeyboardOnFocus(true);
        }
        // Reset the flag after handling
        isProgrammaticFocus.current = false;
        onFocus?.();
    };

    const handleBlur = () => {
        // Delay blur state update to allow clear button press to fire first
        blurTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setIsActive(false);
                onBlur?.();
            }
        }, 100);
    };

    React.useEffect(() => {
        setIsActive(false);
    }, [disabled, invalid]);

    // Cleanup timeouts on unmount to prevent memory leaks
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
            if (clearAnnounceTimeoutRef.current) {
                clearTimeout(clearAnnounceTimeoutRef.current);
            }
        };
    }, []);

    const handleSubmitEditing = () => {
        if (!multiline) {
            if (is_iOS) {
                // Clear any existing timeout before setting a new one
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                // iOS: Blur then refocus without keyboard
                // This avoids Keyboard.dismiss() which crashes in Expo Go v54
                internalRef.current?.blur();
                setShowKeyboardOnFocus(false);

                timeoutRef.current = setTimeout(() => {
                    // Mark this as a programmatic focus so handleFocus doesn't re-enable keyboard
                    isProgrammaticFocus.current = true;
                    internalRef.current?.focus();

                    // Restore VoiceOver focus (iOS accessibility pattern)
                    // This follows the iOS SwiftUI pattern of restoring accessibility focus
                    const reactTag = findNodeHandle(internalRef.current);
                    if (reactTag) {
                        AccessibilityInfo.setAccessibilityFocus(reactTag);
                    }

                    // Keyboard will be re-enabled when user taps (via handlePressIn or next focus)
                }, 10);
            } else {
                // Android: Just blur, let user refocus naturally (works with TalkBack)
                internalRef.current?.blur();
            }
        } else if (multiline && returnKeyType) {
            Keyboard.dismiss();
        }
    };

    const handlePressIn = () => {
        // Ensure keyboard shows on user interaction (iOS only needs this)
        if (is_iOS) {
            setShowKeyboardOnFocus(true);
        }
    };

    const effectiveLabel = label ?? ariaText ?? '';
    const screenReaderErrorText = errorText ? `${errorPrefix ?? ''}${errorText}` : undefined;

    const a11yProps = useMemo<InputAccessibilityProps>(() => {
        const trimmedExplainerText = explainerText?.trim();

        let baseHint: string | undefined = accessibilityHint ?? trimmedExplainerText;
        if (counterLimit !== undefined) {
            const counterText = gt.ngettext(
                // # Screen reader hint announcing current character count vs limit on focus
                '{0} letter out of {1}',
                '{0} letters out of {1}',
                value.length,
                [value.length.toString(), counterLimit.toString()],
            );
            baseHint = baseHint ? `${baseHint}. ${counterText}` : counterText;
        }

        return buildInputAccessibilityProps({
            label: effectiveLabel,
            required,
            requiredText,
            optionalText: optionalText ?? 'optional', // MADS always announces "optional"
            invalid,
            invalidText,
            disabled,
            hasPlaceholder: !!placeholderText,
            hasExternalLabel: false,
            hasValue: !!value?.trim(),
            labelId: label ? labelId : undefined,
            errorMessage: invalid ? errorText : undefined,
            errorPrefix,
            errorMessageId: errorTextId,
            baseHint,
            explainerTextId: trimmedExplainerText ? explainerTextId : undefined,
            controlsId: accessibilityControls,
            ownsId: accessibilityOwns,
            expanded: accessibilityState?.expanded,
            value: value
        });
    }, [
        explainerText,
        effectiveLabel,
        required,
        requiredText,
        optionalText,
        invalid,
        invalidText,
        disabled,
        placeholderText,
        label,
        labelId,
        errorText,
        errorPrefix,
        errorTextId,
        accessibilityHint,
        explainerTextId,
        accessibilityControls,
        accessibilityOwns,
        accessibilityState?.expanded,
        counterLimit,
        value,
        gt,
    ]);

    const debouncedErrorText = useDebounce(invalid ? (screenReaderErrorText ?? '') : '', 2000);

    return (
        <Box sx={containerStyle}>
            {label && (
                <>
                    <Box sx={[styles.labelContainer, disabled && styles.labelDisabled]}>
                        <Label
                            text={label}
                            labelID={labelId}
                            explainerText={explainerText}
                            explainerTextID={explainerTextId}
                            isRequired={required}
                            requiredText={requiredText}
                            optionalText={optionalText}
                            isInvalid={invalid}
                            invalidText={invalidText}
                            testID={`${testID}-label`}
                            // A11Y: Android requires states to be in the label so it may be used by accessibilityLabelledby
                            renderStatesToScreenReader={is_android}
                        />
                    </Box>
                </>
            )}
            <Box sx={wrapperStyle}>
                <View style={styles.inputContainer}>
                    {contentBefore && <Box sx={styles.iconBefore}>{contentBefore}</Box>}
                    <RNTextInput
                        ref={internalRef}
                        value={value}
                        style={[
                            styles.input,
                            disabled && styles.inputDisabled,
                            contentBefore && styles.inputWithContentBefore,
                            contentAfter && styles.inputWithContentAfter,
                            textAreaHeight ? { height: textAreaHeight } : undefined,
                        ]}
                        multiline={multiline}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onPressIn={handlePressIn}
                        autoFocus={autoFocus}
                        editable={!disabled}
                        placeholder={placeholderText}
                        placeholderTextColor={theme.colors.foregroundWeak}
                        onChangeText={onInput}
                        selectionColor={theme.colors.foregroundStrong}
                        keyboardType={keyboardType}
                        autoCapitalize={autoCapitalize}
                        inputAccessoryViewID={inputAccessoryViewID}
                        testID={testID}
                        nativeID={nativeID ?? defaultInputId}
                        returnKeyType={returnKeyType ?? (multiline ? undefined : 'done')}
                        onSubmitEditing={handleSubmitEditing}
                        blurOnSubmit={multiline && !!returnKeyType ? true : false}
                        showSoftInputOnFocus={is_iOS ? showKeyboardOnFocus : undefined}
                        accessibilityRole={accessibilityRole as AccessibilityRole}
                        accessibilityState={
                            { ...accessibilityState, disabled } as AccessibilityState
                        }
                        aria-disabled={disabled}
                        {...a11yProps}
                    />
                    {value.length > 0 && !disabled && showClearButton && isActive && (
                        <TouchableOpacity
                            style={styles.clearButton}
                            onPress={handleClearText}
                            accessibilityLabel={effectiveClearButtonAccessibilityLabel}
                            accessibilityRole="button"
                            focusable
                            {...(Platform.OS === 'web' ? { tabIndex: 0 as never } : {})}
                            testID={`${testID}-clear-button`}
                        >
                            <Close size="sm" color={theme.colors.foregroundStrong} />
                        </TouchableOpacity>
                    )}
                    {contentAfter && <Box sx={styles.iconAfter}>{contentAfter}</Box>}
                </View>
            </Box>

            {counterLimit && (
                <Flex sx={styles.counterContainer}>
                    <CharacterCounter
                        limit={counterLimit}
                        length={value.length}
                        disabled={disabled}
                    />
                </Flex>
            )}

            {invalid && errorText && (
                <Box
                    accessible={true}
                    testID={`${testID}-error-text`}
                    sx={[styles.errorContainer, disabled && styles.errorContainerDisabled]}
                    accessibilityLabel={screenReaderErrorText}
                >
                    <ErrorText nativeID={errorTextId}>{errorText}</ErrorText>
                </Box>
            )}

            {Platform.OS !== 'web' && (
                <LiveRegion>
                    <View style={styles.srOnly} aria-hidden={true}>
                        <Text>{debouncedErrorText}</Text>
                    </View>
                </LiveRegion>
            )}

            {clearAnnouncement ? (
                <LiveRegion>
                    <View style={styles.srOnly}>
                        <Text>{clearAnnouncement}</Text>
                    </View>
                </LiveRegion>
            ) : null}
        </Box>
    );
}
